import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
} from '@backstage/core-components';
import { Box, Chip, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DnsIcon from '@material-ui/icons/Dns';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import StorageIcon from '@material-ui/icons/Storage';
import { Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 3,
    background:
      'linear-gradient(135deg, rgba(0,150,136,0.12), rgba(21,101,192,0.12))',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    marginTop: 'auto',
    paddingTop: theme.spacing(3),
  },
}));

const kubernetesTargets = [
  {
    title: 'C4CI Platform API',
    description: 'Live API workload state for the shared platform runtime.',
    href: '/catalog/default/component/c4ci-platform-api/kubernetes',
    chips: ['AKS dev', 'service', 'c4ci-platform'],
  },
  {
    title: 'C4CI Platform Core',
    description:
      'Live core web workload state for the shared platform runtime.',
    href: '/catalog/default/component/c4ci-platform-core/kubernetes',
    chips: ['AKS dev', 'website', 'c4ci-platform'],
  },
  {
    title: 'C4CI Platform Workers',
    description: 'Live worker workload state for the shared platform runtime.',
    href: '/catalog/default/component/c4ci-platform-workers/kubernetes',
    chips: ['AKS dev', 'service', 'c4ci-platform'],
  },
  {
    title: 'C4CI Admin Hub Runtime',
    description:
      'Live Admin Hub workload state for the shared platform runtime.',
    href: '/catalog/default/component/c4ci-admin-hub-runtime/kubernetes',
    chips: ['AKS dev', 'website', 'c4ci-platform'],
  },
];

export const FactoryKubernetesPage = () => {
  const classes = useStyles();

  return (
    <Page themeId="home">
      <Header
        title="Kubernetes"
        subtitle="Entry point for the live C4CI AKS-backed workload views"
      />
      <Content>
        <Box className={classes.hero}>
          <div className={classes.chipRow}>
            <Chip color="primary" label="C4CI AKS Dev" />
            <Chip label="Entity-scoped views" variant="outlined" />
            <Chip label="Service-account backed in deploy" variant="outlined" />
          </div>
          <Typography variant="h4" component="h2" gutterBottom>
            Kubernetes views live on catalog entities, not on one global admin
            page.
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Use the shortcuts below to open workloads mapped to the C4CI AKS
            development cluster. Local development can still use kubectl proxy,
            but the deployed portal reads the cluster through its own Kubernetes
            service account.
          </Typography>
        </Box>

        <ContentHeader title="Runtime Workloads" />
        <Grid container spacing={3}>
          {kubernetesTargets.map(target => (
            <Grid item xs={12} md={6} key={target.title}>
              <InfoCard className={classes.card} title={target.title}>
                <Box display="flex" alignItems="center" mb={2}>
                  <DnsIcon color="primary" />
                  <Box ml={1}>
                    <Typography variant="body2" color="textSecondary">
                      {target.description}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.chipRow}>
                  {target.chips.map(chip => (
                    <Chip key={chip} label={chip} size="small" />
                  ))}
                </Box>
                <div className={classes.footer}>
                  <Link component={RouterLink} to={target.href}>
                    <Box display="flex" alignItems="center">
                      <OpenInNewIcon fontSize="small" />
                      <Box ml={1}>Open Kubernetes View</Box>
                    </Box>
                  </Link>
                </div>
              </InfoCard>
            </Grid>
          ))}
        </Grid>

        <Box mt={4}>
          <ContentHeader title="Catalog Shortcuts" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard title="Browse All Components">
                <Box display="flex" alignItems="center" mb={2}>
                  <StorageIcon color="primary" />
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Open the catalog and inspect the seeded runtime entities that
                  now carry Kubernetes links.
                </Typography>
                <Link component={RouterLink} to="/">
                  Open Catalog
                </Link>
              </InfoCard>
            </Grid>
          </Grid>
        </Box>
      </Content>
    </Page>
  );
};
